import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Gauge, Wrench, FileText, Car, CheckCircle2, ArrowRight, Clock } from "lucide-react";

const BENEFITS = [
  { icon: Wrench, title: "Poriadok v zákazkách", text: "Každá oprava od naplánovania po úhradu na jednom mieste. Žiadne papiere, žiadne hľadanie." },
  { icon: FileText, title: "Faktúra za 30 sekúnd", text: "Faktúra s DPH a PAY by square QR priamo zo zákazky. Pošlete ju zákazníkovi mailom na klik." },
  { icon: Car, title: "ŠPZ, VIN a história", text: "Vyhľadanie vozidla podľa ŠPZ (SK/CZ) a VIN, kompletná história opráv a prepojenie na zákazníka." },
];

const POINTS = ["14 dní zadarmo", "Bez záväzkov", "Bez inštalácie", "Funguje aj na mobile"];

function Cta({ id }) {
  return (
    <Button asChild size="lg" className="min-h-[52px] px-8 text-base font-semibold" data-testid={id}>
      <Link to="/register">Vyskúšať zadarmo <ArrowRight className="ml-2 h-5 w-5" /></Link>
    </Button>
  );
}

export default function TryLanding() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="try-landing-page">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3">
          <div className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground"><Gauge className="h-5 w-5" /></div>
            <div className="leading-tight">
              <div className="font-heading text-lg font-extrabold uppercase tracking-wide">APIS</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Manager</div>
            </div>
          </div>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground" data-testid="try-login-link">Prihlásiť sa</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-32 left-1/2 h-72 w-[42rem] -translate-x-1/2 rounded-full bg-primary/15 blur-3xl" />
        <div className="relative mx-auto max-w-3xl px-5 py-20 text-center sm:py-28">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary" data-testid="try-badge">
            <Clock className="h-3.5 w-3.5" /> 14 dní zadarmo, bez záväzkov
          </span>
          <h1 className="mt-6 font-heading text-4xl font-extrabold uppercase leading-[1.05] sm:text-5xl lg:text-6xl">
            Celý autoservis<br /><span className="text-primary">pod kontrolou</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base text-muted-foreground sm:text-lg">
            Zákazky, faktúry, vozidlá aj plánovač na jednom mieste. Softvér vytvorený pre slovenské autoservisy – s DPH, QR platbou a vyhľadávaním ŠPZ.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Cta id="try-hero-cta" />
            <span className="text-sm text-muted-foreground">Rozbehnete to za jedno popoludnie.</span>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {POINTS.map((p) => (
              <span key={p} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground" data-testid={`try-point-${p}`}>
                <CheckCircle2 className="h-4 w-4 text-primary" /> {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="mx-auto max-w-5xl px-5 pb-8">
        <div className="grid gap-5 sm:grid-cols-3">
          {BENEFITS.map((b) => (
            <div key={b.title} className="rounded-2xl border border-border bg-card/60 p-6 transition-colors hover:border-primary/40" data-testid={`try-benefit-${b.title}`}>
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary"><b.icon className="h-6 w-6" /></div>
              <h3 className="mt-4 font-heading text-lg font-bold uppercase">{b.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-3xl px-5 py-20 text-center">
        <h2 className="font-heading text-3xl font-extrabold uppercase sm:text-4xl">Pripravení zjednodušiť si servis?</h2>
        <p className="mx-auto mt-3 max-w-lg text-muted-foreground">Vytvorte si účet zadarmo a začnite v demo režime s ukážkovými dátami. Platobná karta sa overuje až pri predplatnom.</p>
        <div className="mt-8"><Cta id="try-footer-cta" /></div>
        <p className="mt-4 text-sm text-muted-foreground">Máte už účet? <Link to="/login" className="text-primary hover:underline" data-testid="try-login-link-2">Prihlásiť sa</Link></p>
      </section>

      <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} APIS Manager ·{" "}
        <Link to="/obchodne-podmienky" className="hover:text-foreground hover:underline">Obchodné podmienky</Link>
        {" · "}
        <Link to="/ochrana-osobnych-udajov" className="hover:text-foreground hover:underline">Ochrana osobných údajov</Link>
      </footer>
    </div>
  );
}
